import { timestampDate } from '@bufbuild/protobuf/wkt'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useParams } from 'react-router'

import { Box, HStack, Link, Text } from '@enechain/ecloud-designsystem'
import { FaChevronLeftRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { iconSize } from '~/constants/iconSize'
import { formatJstDateDisplay } from '~/models/dateTime'
import { AnnouncementMarkdown } from '~/pages/AnnouncementDetail/components/AnnouncementMarkdown'
import {
  isAnnouncementNotFound,
  useGetAnnouncement,
} from '~/pages/AnnouncementDetail/hooks/useGetAnnouncement'
import { routePaths } from '~/routes/routePaths'

const BackToListLink = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Link asChild>
      <RouterLink to={routePaths.announcements.path}>
        <HStack gap={1}>
          <FaChevronLeftRegular boxSize={iconSize.labeledButton} />
          {t('page:announcementDetail.backToList')}
        </HStack>
      </RouterLink>
    </Link>
  )
}

/**
 * お知らせ詳細。design に詳細画面は無く、Design Note D-UI-1 / D-UI-2 の最小形
 * (見出し = index の title、公開日、本文 markdown、一覧への導線)。
 */
export const AnnouncementDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { announcementId } = useParams<{ announcementId: string }>()
  const { data, error, isLoading } = useGetAnnouncement(announcementId)
  const announcement = data?.announcement

  const breadcrumbItems = [
    {
      label: t('common:route.announcements'),
      to: routePaths.announcements.path,
    },
    {
      label: announcement?.title ?? t('common:route.announcementDetail'),
      bold: true,
    },
  ]

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        <BackToListLink />
        <PageScrollArea>
          {isAnnouncementNotFound(error) ? (
            <CompactAlert
              description={t('page:announcementDetail.notFound.description')}
              status="warning"
              title={t('page:announcementDetail.notFound.title')}
            />
          ) : (
            <RegionLoading isLoading={isLoading || announcement === undefined}>
              {isLoading || announcement === undefined ? null : (
                <Box as="article">
                  <Text as="h2" textStyle="pageTitle" marginBottom={1}>
                    {announcement.title}
                  </Text>
                  {announcement.publishedAt !== undefined && (
                    <Text
                      textStyle="captionNormal"
                      marginBottom={4}
                      color="Text.midEmphasis"
                    >
                      {formatJstDateDisplay(
                        timestampDate(announcement.publishedAt),
                      )}
                    </Text>
                  )}
                  <AnnouncementMarkdown markdown={announcement.bodyMarkdown} />
                </Box>
              )}
            </RegionLoading>
          )}
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
